/**
 * The focus primitives every interactive element is wrapped in
 * (`03-client-app.md` section 3.2).
 *
 * `@noriginmedia/norigin-spatial-navigation` does the directional movement. This file adds the
 * parts the spec asks for on top: the focus ring only shows for keyboard and gamepad, A activates,
 * the focused element scrolls into view, and modals trap focus with B closing them.
 */

import {
  FocusContext,
  useFocusable,
  setFocus,
  type UseFocusableConfig,
} from '@noriginmedia/norigin-spatial-navigation';
import * as React from 'react';
import { lastInputSource, onInputSourceChange, type InputSource } from '../../gamepad/input.js';
import { useGamepadButton } from './useGamepad.js';
import { revealFocused } from './reveal.js';

/** True while the focus ring should be visible. */
export function useShowFocusRing(): boolean {
  const [source, setSource] = React.useState<InputSource>(lastInputSource);
  React.useEffect(() => onInputSourceChange(setSource), []);
  return source !== 'pointer';
}

export interface FocusableProps {
  children: React.ReactNode;
  /**
   * Runs on A, on Enter and on click.
   *
   * On click too, which is the part that is easy to forget: the wrapper carries the handler, so
   * a button inside it must not carry one as well or the action happens twice. That is why
   * every call site in this app reads `<Focusable onSelect={f}><Button>…</Button></Focusable>`
   * with nothing on the button.
   */
  onSelect?: () => void;
  /** The X action, shown in the hints bar while this element is focused. */
  onSecondary?: () => void;
  /** Label for the X action, shown in the hints bar. */
  secondaryLabel?: string;
  /** Label for the A action. Defaults to "Select". */
  selectLabel?: string;
  disabled?: boolean;
  /** Give this element focus when its screen opens. One per route. */
  autoFocus?: boolean;
  focusKey?: string;
  className?: string;
  onFocus?: () => void;
  /**
   * What a direction means while this element is focused. Return false to keep it here.
   *
   * It belongs on the element rather than on the zone around it: the library asks whatever holds
   * focus, and a zone only holds focus in the moment before it hands it to a child. A carousel
   * that wants left and right to walk it puts this on the control the thumb is resting on.
   */
  onArrowPress?: (direction: string) => boolean;
  /**
   * True when what is wrapped is a real text field rather than a control.
   *
   * It only changes what the wrapper claims to be. A `div` that says `role="button"` around a
   * `textarea` is announced as a button, so a screen reader offers to press the description
   * instead of letting anyone write it, and the whole box is one stop rather than a field with
   * a caret in it. The wrapper still moves focus and still takes A, because that is how a
   * controller reaches the field at all — see `FocusableTextField`.
   */
  field?: boolean;
}

export interface FocusHints {
  selectLabel?: string;
  secondaryLabel?: string;
}

const HintsContext = React.createContext<{
  set: (hints: FocusHints | null) => void;
  hints: FocusHints | null;
}>({ set: () => {}, hints: null });

export function HintsProvider({ children }: { children: React.ReactNode }) {
  const [hints, setHints] = React.useState<FocusHints | null>(null);
  const value = React.useMemo(() => ({ hints, set: setHints }), [hints]);
  return <HintsContext.Provider value={value}>{children}</HintsContext.Provider>;
}

export function useHints(): FocusHints | null {
  return React.useContext(HintsContext).hints;
}

export function Focusable({
  children,
  onSelect,
  onSecondary,
  secondaryLabel,
  selectLabel,
  disabled = false,
  autoFocus = false,
  focusKey,
  className,
  onFocus,
  onArrowPress,
  field = false,
}: FocusableProps) {
  const { set: setHints } = React.useContext(HintsContext);
  const showRing = useShowFocusRing();

  const config: UseFocusableConfig<unknown> = {
    focusKey,
    focusable: !disabled,
    onEnterPress: () => onSelect?.(),
    onArrowPress: onArrowPress ? (dir) => onArrowPress(dir) : undefined,
    onFocus: () => {
      onFocus?.();
      setHints({ selectLabel, secondaryLabel: onSecondary ? secondaryLabel : undefined });
    },
  };

  const { ref, focused, focusSelf, focusKey: resolvedKey } = useFocusable(config);

  React.useEffect(() => {
    if (autoFocus && !disabled) focusSelf();
  }, [autoFocus, disabled, focusSelf]);

  // Keep the focused element on screen without jumping the page around.
  React.useEffect(() => {
    if (!focused) return;
    const node = ref.current as HTMLElement | null;
    if (node) revealFocused(node);
  }, [focused, ref]);

  // X is the secondary action of whatever is focused right now.
  useGamepadButton('x', () => onSecondary?.(), focused && !disabled && Boolean(onSecondary));

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      className={[
        'gb-focusable',
        focused && showRing ? 'gb-focusable--focused' : '',
        disabled ? 'gb-focusable--disabled' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-focused={focused || undefined}
      // The library's own key for this element, put where the DOM can be searched by it. Paging
      // works out where to land by measuring boxes on screen, and then has to name what it found
      // to something that only knows focus keys.
      data-focus-key={resolvedKey}
      onClick={disabled ? undefined : onSelect}
      role={onSelect && !field ? 'button' : undefined}
      tabIndex={disabled || !onSelect || field ? undefined : -1}
      aria-disabled={disabled || undefined}
    >
      {children}
    </div>
  );
}

export interface FocusZoneProps {
  children: React.ReactNode;
  focusKey: string;
  className?: string;
  /** Keeps focus inside. Used by modals and drawers. */
  trap?: boolean;
  /**
   * How this zone is laid out.
   *
   * Movement itself is geometric — the library walks to whatever is nearest the way you pushed —
   * so this constrains nothing. It says which way the zone is meant to be read, for the DOM and
   * for whoever is next in here. A direction a zone has none of leaves it, which is right: a row
   * has no up, a column has no left, and in both cases stepping out beats doing nothing.
   */
  direction?: 'row' | 'column' | 'grid';
  /** Remembers the last focused child and returns to it. */
  preserve?: boolean;
  style?: React.CSSProperties;
}

/**
 * Groups focusable elements.
 *
 * Every screen and every list is a zone, which is what stops focus crossing from a grid into the
 * navigation sideways when it should move within the grid.
 *
 * A zone does not get to say what a direction means. The library asks whatever holds focus, and
 * a zone only holds focus for the instant before it hands it down to a child, so a handler here
 * would never run. That lives on `Focusable`, on the element the thumb is actually resting on.
 */
export function FocusZone({
  children,
  focusKey,
  className,
  trap = false,
  direction = 'grid',
  preserve = true,
  style,
}: FocusZoneProps) {
  const {
    ref,
    focusKey: resolvedKey,
    focusSelf,
    hasFocusedChild,
  } = useFocusable({
    focusKey,
    isFocusBoundary: trap,
    focusBoundaryDirections: trap ? ['up', 'down', 'left', 'right'] : undefined,
    saveLastFocusedChild: preserve,
    trackChildren: true,
  });

  // A trap that loses its focused child pulls focus straight back in.
  React.useEffect(() => {
    if (trap && !hasFocusedChild) focusSelf();
  }, [trap, hasFocusedChild, focusSelf]);

  return (
    <FocusContext.Provider value={resolvedKey}>
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={className}
        style={style}
        data-axis={direction}
      >
        {children}
      </div>
    </FocusContext.Provider>
  );
}

/** Moves focus to a known key, used when a screen opens or a dialog closes. */
export function focusTo(focusKey: string): void {
  setFocus(focusKey);
}
